/* Full source first; the existing catalogue owns ranking, rendering and interaction. */
(() => {
  const script = document.currentScript;
  const root = script?.closest('vorug-infinite-collection[data-vorug-bootstrap="pending"]');
  if (!root || root.dataset.vorugBootstrapStarted) return;
  root.dataset.vorugBootstrapStarted = 'true';
  const controller = new AbortController();
  const loading = root.querySelector('[data-vorug-bootstrap-status]');
  root.setAttribute('aria-busy', 'true');
  if (loading) loading.hidden = false;
  const errorLink = root.querySelector('[data-load-error]');
  const status = root.querySelector('[data-status]');
  const inserted = [];
  let complete = false, executionStarted = false, timeout;
  const detached = new MutationObserver(() => {
    if (!root.isConnected) controller.abort();
  });
  detached.observe(document.documentElement, { childList: true, subtree: true });

  const placeRetry = () => {
    if (!errorLink || !root.isConnected) return;
    const headerBottom = document.getElementById('vn19-site-header')?.getBoundingClientRect().bottom || 0;
    errorLink.style.top = `${Math.max(0, headerBottom - root.getBoundingClientRect().top) + 20}px`;
  };

  const restoreGallery = () => {
    if (!root.isConnected || root.querySelector('[data-vorug-gallery-recovery]')) return;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', restoreGallery, { once: true });
      return;
    }
    const fallback = root.querySelector('noscript');
    if (!fallback) return;
    const template = document.createElement('template');
    template.innerHTML = fallback.textContent;
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-vorug-gallery-recovery', '');
    wrapper.append(template.content);
    root.append(wrapper);
    placeRetry();
  };

  const recover = error => {
    root.dataset.vorugBootstrapState = 'failed';
    root.removeAttribute('aria-busy');
    loading?.remove();
    if (status) status.textContent = 'The interactive catalogue could not load. A paginated gallery is available below. Retry to restore the interactive view.';
    if (errorLink) {
      errorLink.hidden = false;
      errorLink.href = location.href;
      errorLink.textContent = 'Interactive catalogue unavailable. Browse below or retry.';
      placeRetry();
    }
    if (executionStarted) root.disconnectedCallback?.();
    for (const node of inserted) node.remove();
    restoreGallery();
    root.dispatchEvent(new CustomEvent('vorug:bootstrap-error', { detail: { reason: error.name || 'Error' } }));
  };

  const start = async () => {
    const config = JSON.parse(root.querySelector('[data-vorug-bootstrap-config]').textContent);
    if (!config.section || config.section !== root.dataset.section || config.sourceSection !== 'vorug-catalogue-source' || !Array.isArray(config.scripts) || config.scripts.length !== 10) throw new Error('Invalid catalogue boot configuration');
    const urls = config.scripts.map(value => {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin || !/^\/cdn\/shop\/t\/\d+\/assets\/[\w-]+\.js$/.test(url.pathname)) throw new Error('Invalid owned catalogue asset');
      return url.href;
    });
    if (new Set(urls).size !== urls.length) throw new Error('Duplicate catalogue assets');
    const sourceURL = new URL(location.href);
    sourceURL.searchParams.delete('sections');
    sourceURL.searchParams.set('section_id', config.sourceSection);
    sourceURL.searchParams.set('page', '1');
    timeout = setTimeout(() => controller.abort(), 20000);
    const response = await fetch(sourceURL.href, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
    if (!response.ok || !/^text\/html(?:;|$)/i.test(response.headers.get('content-type') || '')) throw new Error('Catalogue source unavailable');
    const receivedURL = new URL(response.url);
    if (receivedURL.origin !== location.origin || receivedURL.pathname !== sourceURL.pathname) throw new Error('Catalogue source context changed');
    const documentSource = new DOMParser().parseFromString(await response.text(), 'text/html');
    const receivedRoot = documentSource.querySelector('[data-vorug-catalogue-source]');
    const source = receivedRoot?.querySelector('[data-source]');
    if (!receivedRoot || receivedRoot.dataset.section !== config.sourceSection || source?.dataset.sourcePageSize !== '250' || receivedRoot.hasAttribute('data-vorug-bootstrap')) throw new Error('Incomplete catalogue source boundary');
    for (const key of ['entryRoute', 'entryShape', 'entryStyle', 'entryColor', 'entryOrigin', 'entryConstruction', 'fullCatalogue', 'canonicalTaxonomy', 'includeUnavailable']) {
      if (receivedRoot.dataset[key] !== root.dataset[key]) throw new Error('Catalogue source scope changed');
    }
    const anchors = [...source.children];
    if (anchors.some(node => node.tagName !== 'A' || !node.dataset.productId || !node.querySelector('img[data-src]')) || new Set(anchors.map(node => node.dataset.productId)).size !== anchors.length) throw new Error('Invalid catalogue source rows');
    if (source.dataset.next) {
      const next = new URL(source.dataset.next, sourceURL);
      if (next.origin !== location.origin || next.pathname !== sourceURL.pathname) throw new Error('Catalogue next-page context changed');
    }
    if (controller.signal.aborted || !root.isConnected) throw new DOMException('Aborted', 'AbortError');
    if (document.readyState === 'loading') await new Promise((resolve, reject) => {
      document.addEventListener('DOMContentLoaded', resolve, { once: true, signal: controller.signal });
      controller.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
    });
    if (controller.signal.aborted || !root.isConnected) throw new DOMException('Aborted', 'AbortError');
    const target = root.querySelector('[data-source]');
    target.replaceChildren(...anchors);
    target.dataset.next = source.dataset.next || '';
    target.dataset.sourcePageSize = '250';
    delete target.dataset.pageRecoveryUrl;
    root.dataset.vorugBootstrapState = 'initializing';
    executionStarted = true;
    let loaded = 0;
    await new Promise((resolve, reject) => {
      const runtimeError = event => {
        if (event.filename && urls.includes(new URL(event.filename, location.href).href)) reject(new Error('Catalogue initialization failed'));
      };
      window.addEventListener('error', runtimeError, { signal: controller.signal });
      controller.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
      // async=false keeps execution order; all requests are inserted in this task.
      for (const url of urls) {
        const node = document.createElement('script');
        node.async = false;
        node.src = url;
        node.onload = () => {
          if (url !== urls[loaded]) return reject(new Error('Catalogue script order changed'));
          if (++loaded === urls.length) resolve();
        };
        node.onerror = () => reject(new Error('Catalogue asset unavailable'));
        inserted.push(node);
        document.head.append(node);
      }
    });
    if (controller.signal.aborted || !root.isConnected || !root.initialized || typeof root.schedule !== 'function') throw new Error('Catalogue initialization incomplete');
    if (new URLSearchParams(location.search).get('journey') !== 'off' && root.dataset.journeyReady !== 'true') throw new Error('Catalogue journey initialization incomplete');
    complete = true;
    root.dataset.vorugBootstrapState = 'ready';
    delete root.dataset.vorugBootstrap;
    root.removeAttribute('aria-busy');
    loading?.remove();
    root.schedule();
    root.dispatchEvent(new CustomEvent('vorug:bootstrap-ready'));
  };
  start().catch(error => {
    controller.abort();
    if (root.isConnected) recover(error);
  }).finally(() => {
    clearTimeout(timeout);
    detached.disconnect();
    if (complete) controller.abort();
  });
})();
